<section *ngIf="!showActivationError(); else pageError">
  @if (query.isPending()) {
  <div class="py-8 space-y-4">
    <nz-skeleton
      [nzActive]="true"
      [nzParagraph]="true"
      [nzRound]="true"
    ></nz-skeleton>
    <nz-skeleton
      [nzActive]="true"
      [nzParagraph]="true"
      [nzRound]="true"
    ></nz-skeleton>
    <nz-skeleton
      [nzActive]="true"
      [nzParagraph]="true"
      [nzRound]="true"
    ></nz-skeleton>
    <nz-skeleton
      [nzActive]="true"
      [nzParagraph]="true"
      [nzRound]="true"
    ></nz-skeleton>
    <nz-skeleton
      [nzActive]="true"
      [nzParagraph]="true"
      [nzRound]="true"
    ></nz-skeleton>
  </div>

  } @else {
  <div *ngIf="query.isSuccess()">
    <div *ngIf="query.isFetched; else accountSetupAlreadyCompleted">
        <h1
          class="mb-4 -mt-3 text-2xl font-extrabold leading-snug tracking-tight text-left md:text-4xl onboarding-heading"
        >
          Complete your account setup
        </h1>

        <!-- Email Verification Step -->
        @if (emailVerificationStep() === 'verifying') {
          <div class="text-center py-8">
            <nz-spin nzSimple [nzSize]="'large'"></nz-spin>
            <p class="mt-4 text-lg onboarding-muted">Verifying your email address...</p>
          </div>
        }

        @if (emailVerificationStep() === 'verified') {
          <nz-alert
            nzType="success"
            nzMessage="✅ Email Verified!"
            nzDescription="Your email address has been verified. Please set your password below."
            [nzCloseable]="false"
            class="mb-6"
          ></nz-alert>

          <nz-alert
            *ngIf="user$ | async as user"
            [nzDescription]="
              'Please set a password to activate access for ' +
              user.company.name
            "
            [nzMessage]="'Hello ' + user.name"
          ></nz-alert>
        }

        @if (emailVerificationStep() === 'error') {
          <app-error-display
            [error]="activationErrorDetails"
            [showRetry]="true"
            (retry)="verifyEmail(activationToken()!)"
          />
        }

        <form
          #accountForm
          *ngIf="emailVerificationStep() === 'verified'"
          (ngSubmit)="completeAccountSetup()"
          [formGroup]="form"
          class="mt-8 space-y-4"
          novalidate
        >
          <label class="block">
            <span class="block mb-1 text-sm font-medium onboarding-label"
              >Your Account Email is</span
            >
            <input
              class="form-input input input-bordered input-md rounded-lg w-full"
              autocomplete="username"
              formControlName="email"
              inputmode="email"
              name="email"
              placeholder="Ex. james@bond.com"
              required
              type="email"
            />
          </label>
          <label class="block">
            <span class="block mb-1 text-sm font-medium onboarding-label"
              >Your Password</span
            >
            <div class="relative">
              <input
                class="form-input input input-md input-bordered rounded-lg w-full pr-24"
                autocomplete="new-password"
                formControlName="password"
                name="password"
                [type]="isPasswordVisible() ? 'text' : 'password'"
                required
                aria-label="Password"
              />
              <button
                type="button"
                tabindex="-1"
                class="btn btn-ghost btn-xs absolute right-2 top-1/2 -translate-y-1/2"
                (click)="isPasswordVisible.set(!isPasswordVisible())"
                aria-label="Toggle password visibility"
              >
                {{ isPasswordVisible() ? 'Hide' : 'Show' }}
              </button>
            </div>
            <div class="mt-2">
              <div class="grid grid-cols-4 gap-2">
                <div
                  class="h-1 rounded password-streak"
                  [ngClass]="
                    passwordStrength() > 0 ? 'password-streak--active' : ''
                  "
                ></div>
                <div
                  class="h-1 rounded password-streak"
                  [ngClass]="
                    passwordStrength() > 1 ? 'password-streak--active' : ''
                  "
                ></div>
                <div
                  class="h-1 rounded password-streak"
                  [ngClass]="
                    passwordStrength() > 2 ? 'password-streak--active' : ''
                  "
                ></div>
                <div
                  class="h-1 rounded password-streak"
                  [ngClass]="
                    passwordStrength() > 3 ? 'password-streak--active' : ''
                  "
                ></div>
              </div>
              <small class="onboarding-muted"
                >Use 8+ chars with letters and numbers.</small
              >
            </div>
            <div
              *ngIf="
                form.get('password')?.touched && form.get('password')?.invalid
              "
              class="onboarding-error text-xs mt-1"
            >
              <div *ngIf="form.get('password')?.errors?.['required']">
                Password is required.
              </div>
              <div *ngIf="form.get('password')?.errors?.['minlength']">
                Minimum 8 characters.
              </div>
              <div *ngIf="form.get('password')?.errors?.['pattern']">
                Include letters and numbers.
              </div>
            </div>
          </label>
          <label class="block">
            <span class="block mb-1 text-sm font-medium onboarding-label"
              >Confirm Password</span
            >
            <div class="relative">
              <input
                class="form-input input input-md input-bordered rounded-lg w-full pr-24"
                autocomplete="new-password"
                formControlName="passwordConfirm"
                name="passwordConfirm"
                [type]="isPasswordConfirmVisible() ? 'text' : 'password'"
                required
                aria-label="Confirm Password"
              />
              <button
                type="button"
                tabindex="-1"
                class="btn btn-ghost btn-xs absolute right-2 top-1/2 -translate-y-1/2"
                (click)="
                  isPasswordConfirmVisible.set(!isPasswordConfirmVisible())
                "
                aria-label="Toggle confirm password visibility"
              >
                {{ isPasswordConfirmVisible() ? 'Hide' : 'Show' }}
              </button>
            </div>
            <div
              *ngIf="
                form.get('passwordConfirm')?.touched &&
                form.get('passwordConfirm')?.invalid
              "
              class="onboarding-error text-xs mt-1"
            >
              <div *ngIf="form.get('passwordConfirm')?.errors?.['required']">
                Confirm your password.
              </div>
            </div>
          </label>
          <div>
            <button
              [disabled]="isSubmitting()"
              class="w-full mt-8 btn btn-primary btn-lg"
              type="submit"
              value="Register"
              aria-busy="isSubmitting()"
            >
              {{ isSubmitting() ? 'Submitting…' : 'Complete Account' }}
            </button>
          </div>
        </form>
        <div
          class="pt-6 mt-6 text-sm font-medium border-t onboarding-footer"
        >
          Already have an account?
          <a
            class="onboarding-link"
            routerLink="/passport/login"
            >Login</a
          >
        </div>
        <ngx-loading [show]="isSubmitting()"></ngx-loading>
    </div>

    <ng-template #accountSetupAlreadyCompleted>
      <nz-result
        nzStatus="success"
        nzSubTitle="Your account setup has already been completed. Please sign in to continue or request for password reset if you have forgotten your password"
        nzTitle="Invalid Activation Request!"
      >
        <div nz-result-extra>
          <button class="btn btn-primary" routerLink="/passport/login">
            Sign In
          </button>
          <button class="btn btn-ghost" routerLink="/passport/reset-password">
            Reset my password
          </button>
        </div>
      </nz-result>
    </ng-template>
  </div>
  }
</section>

<ng-template #pageError>
  <div
    class="py-12 flex flex-col items-center justify-center"
  >
    <nz-result
      nzStatus="500"
      nzTitle="Ops! Something went wrong"
      [nzSubTitle]="activationErrorDetails?.detail ?? 'The activation link is broken or already used.'"
    >
      <div nz-result-extra>
        <div class="onboarding-muted mb-4">
          If the problem persists, contact technical support for assistance.
        </div>
        <button
          class="btn btn-outline btn-primary"
          routerLink="/passport/login"
        >
          Login
        </button>
      </div>
    </nz-result>
  </div>
</ng-template>
